import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type CdwDialogType = 'warning' | 'info' | 'error' | 'success' | 'question';
export type CdwDialogSize = 'small' | 'medium' | 'large';
export type CdwDialogLayout = 'default' | 'warning-box'; // warning-box: left icon with bordered box

export interface CdwConfirmationDialogData {
  title: string;
  message: string;
  type?: CdwDialogType;
  size?: CdwDialogSize;
  layout?: CdwDialogLayout; // Use 'warning-box' for red border, yellow fill, left icon
  icon?: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonColor?: 'primary' | 'accent' | 'warn';
  showCancelButton?: boolean;
  hard?: boolean; // If true, hide the X close button
  fontFamily?: string; // Custom font family for the message text and checkbox
  showDontShowAgainCheckbox?: boolean; // If true, show "Don't show again" checkbox
  dontShowAgainText?: string; // Custom text for the checkbox label
  htmlMessage?: string; // If provided, renders HTML content instead of plain text message
  linkUrl?: string; // URL for the help desk link
  linkText?: string; // Text to display for the link
  hideIcon?: boolean; // If true, hide the dialog icon
  contentPadding?: string; // Custom padding for dialog content (e.g., '16px 24px' or '1rem 2rem 1.5rem 2rem')
  // Title styling
  titleColor?: string; // Custom color for the title text (e.g., '#ffffff', 'white')
  titleFontFamily?: string; // Custom font family for the title text
  titleFontSize?: string; // Custom font size for the title text (e.g., '1.5rem', '24px')
  titleFontWeight?: string | number; // Custom font weight for the title text (e.g., '700', 'bold')
  titleBackgroundColor?: string; // Custom background color for the title header (e.g., '#ffffff', 'white', 'var(--color-primary-white)')
  // Message styling
  messageColor?: string; // Custom color for the message text (e.g., '#333', 'rgb(51, 51, 51)')
  messageFontSize?: string; // Custom font size for the message text (e.g., '1rem', '16px')
  messageFontWeight?: string | number; // Custom font weight for the message text (e.g., '400', 'bold', 700)
  // Link styling
  linkColor?: string; // Custom color for hyperlinks (e.g., '#2196f3', 'blue')
  // Input validation
  requireInputConfirmation?: boolean; // If true, show an input field that requires user to type a specific text
  inputLabel?: string; // Label for the confirmation input field
  requiredText?: string; // The exact text that must be typed to enable the confirm button
  // Textarea support
  useTextarea?: boolean; // If true, shows a textarea instead of a single-line input (only applies when requireInputConfirmation is true)
  textareaRows?: number; // Number of rows for the textarea (default: 10)
  textareaPlaceholder?: string; // Placeholder text for the textarea
  // Actions styling
  hideActionsBorder?: boolean; // If true, hides the border above the action buttons (default: false)
}

@Component({
  selector: 'lib-cdw-confirmation-dialog',
  templateUrl: './cdw-confirmation-dialog.component.html',
  styleUrls: ['./cdw-confirmation-dialog.component.scss'],
  standalone: false,
})
export class CdwConfirmationDialogComponent {
  public dialogIcon: string;
  public dialogIconClass: string;
  public dialogSize: string;
  public confirmButtonColor: 'primary' | 'accent' | 'warn';
  public showCancelButton: boolean;
  public isWarningBoxLayout: boolean;
  public dontShowAgain = false;
  public confirmationInput = '';
  public inputLabel = '';

  constructor(
    public dialogRef: MatDialogRef<CdwConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CdwConfirmationDialogData
  ) {
    // Set defaults
    this.data.type = this.data.type || 'info';
    this.data.size = this.data.size || 'medium';
    this.data.layout = this.data.layout || 'default';
    this.data.confirmText = this.data.confirmText || 'Confirm';
    this.data.cancelText = this.data.cancelText || 'Cancel';
    this.data.showCancelButton = this.data.showCancelButton ?? true;
    this.data.hard = this.data.hard ?? false;
    this.data.showDontShowAgainCheckbox = this.data.showDontShowAgainCheckbox ?? false;
    this.data.dontShowAgainText = this.data.dontShowAgainText || "Don't show again";
    this.data.hideIcon = this.data.hideIcon ?? false;
    this.data.requireInputConfirmation = this.data.requireInputConfirmation ?? false;
    this.data.inputLabel = this.data.inputLabel || `Type "${this.data.requiredText || 'CONFIRM'}" to continue`;
    this.inputLabel = this.data.inputLabel;
    this.data.useTextarea = this.data.useTextarea ?? false;
    this.data.textareaRows = this.data.textareaRows || 10;
    this.data.textareaPlaceholder = this.data.textareaPlaceholder || '';

    // Set icon based on type if not provided
    if (this.data.icon) {
      this.dialogIcon = this.data.icon;
    } else {
      this.dialogIcon = this.getDefaultIcon(this.data.type);
    }

    // Set icon class based on type
    this.dialogIconClass = `dialog-icon-${this.data.type}`;

    // Set dialog size class
    this.dialogSize = `dialog-${this.data.size}`;

    // Set confirm button color
    this.confirmButtonColor = this.data.confirmButtonColor || this.getDefaultButtonColor(this.data.type);

    // Set cancel button visibility
    this.showCancelButton = this.data.showCancelButton;

    // Set layout flag
    this.isWarningBoxLayout = this.data.layout === 'warning-box';
  }

  private getDefaultIcon(type: CdwDialogType): string {
    const iconMap: Record<CdwDialogType, string> = {
      warning: 'warning',
      error: 'error',
      success: 'check_circle',
      info: 'info',
      question: 'help',
    };
    return iconMap[type];
  }

  private getDefaultButtonColor(type: CdwDialogType): 'primary' | 'accent' | 'warn' {
    const colorMap: Record<CdwDialogType, 'primary' | 'accent' | 'warn'> = {
      warning: 'warn',
      error: 'warn',
      success: 'primary',
      info: 'primary',
      question: 'primary',
    };
    return colorMap[type];
  }

  public onConfirm(): void {
    if (this.isConfirmDisabled()) {
      return;
    }
    this.dialogRef.close({
      confirmed: true,
      dontShowAgain: this.dontShowAgain,
      inputValue: this.data.useTextarea ? this.confirmationInput : undefined
    });
  }

  public onCancel(): void {
    this.dialogRef.close({
      confirmed: false,
      dontShowAgain: this.dontShowAgain
    });
  }

  public onDismiss(): void {
    this.dialogRef.close({
      confirmed: false,
      dontShowAgain: this.dontShowAgain
    });
  }

  public isConfirmDisabled(): boolean {
    if (!this.data.requireInputConfirmation) {
      return false;
    }
    // For textarea mode, just check if there's any input
    if (this.data.useTextarea) {
      return !this.confirmationInput || this.confirmationInput.trim().length === 0;
    }
    // For regular input mode, validate against requiredText
    return this.confirmationInput !== this.data.requiredText;
  }
}
